/**
 * Global app state. Two stores:
 *   - {@link authStore} — JWT pair + cached `/me` profile, persisted to LS.
 *   - {@link prefsStore} — non-secret UI prefs (last device id).
 *
 * Zustand is used over Context for ergonomics: imperative `getState()` is
 * needed from non-React code (api.ts, ws.ts).
 */

import { create } from "zustand";
import type { TokenPair, UserOut } from "./api";

const LS_TOKENS = "tw.tokens";
const LS_USER = "tw.user";
const LS_DEVICE_ID = "tw.deviceId";

/**
 * Defensive bootstrap: if persisted LS state belongs to a demo account
 * (email matches the throwaway anon pattern), purge it on startup. Earlier
 * versions of the demo flow accidentally let rotated tokens land in
 * localStorage; this self-heal keeps existing visitors from being stuck on
 * a "logged-in-as-demo" screen forever.
 */
function purgeDemoLeftoversOnBoot(): void {
  try {
    const u = loadJson<UserOut>(LS_USER);
    const email = (u?.email ?? "").toLowerCase();
    if (email.endsWith("@aevum.demo")) {
      localStorage.removeItem(LS_TOKENS);
      localStorage.removeItem(LS_USER);
    }
  } catch { /* ignore */ }
}
purgeDemoLeftoversOnBoot();

type AuthState = {
  tokens: TokenPair | null;
  user: UserOut | null;
  /**
   * True while the user is in the in-memory "Try demo" flow. Demo tokens are
   * NOT persisted to localStorage and the throwaway account is deleted on
   * exit. UI uses this flag to render the demo shell instead of the normal
   * authenticated app, and to hide history/settings/etc.
   */
  isDemo: boolean;
  /** Persistent token set — written to localStorage. Used by real sign-in/up. */
  setTokens(t: TokenPair): void;
  /**
   * Ephemeral token set used only for the demo. Does NOT touch localStorage
   * so closing the tab cleanly evicts it. Pair with {@link setDemo}(true).
   */
  setDemoTokens(t: TokenPair): void;
  setUser(u: UserOut | null): void;
  setDemo(v: boolean): void;
  logout(): void;
};

export const authStore = create<AuthState>((set, get) => ({
  tokens: loadJson<TokenPair>(LS_TOKENS),
  user: loadJson<UserOut>(LS_USER),
  isDemo: false,
  setTokens(t) {
    // Honour demo mode: token rotation via `/auth/refresh` reaches this
    // setter too (see `api.tryRefresh`) and we must NOT let rotated demo
    // tokens leak into localStorage, or a hard refresh restores the demo
    // session instead of dropping the visitor on AuthScreen.
    if (get().isDemo) {
      set({ tokens: t });
      return;
    }
    localStorage.setItem(LS_TOKENS, JSON.stringify(t));
    set({ tokens: t });
  },
  setDemoTokens(t) {
    // Intentionally NOT written to localStorage.
    set({ tokens: t, isDemo: true });
  },
  setUser(u) {
    // When in demo mode we also avoid persisting the cached /me, so a hard
    // reload always lands on the public AuthScreen.
    const demo = get().isDemo;
    if (u && !demo) localStorage.setItem(LS_USER, JSON.stringify(u));
    else if (!u) localStorage.removeItem(LS_USER);
    set({ user: u });
  },
  setDemo(v) {
    set({ isDemo: v });
  },
  logout() {
    localStorage.removeItem(LS_TOKENS);
    localStorage.removeItem(LS_USER);
    set({ tokens: null, user: null, isDemo: false });
  },
}));

type PrefsState = {
  deviceId: string;
};

export const prefsStore = create<PrefsState>(() => {
  return {
    deviceId: loadOrCreateDeviceId(),
  };
});

function loadJson<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

function loadOrCreateDeviceId(): string {
  let id = localStorage.getItem(LS_DEVICE_ID);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(LS_DEVICE_ID, id);
  }
  return id;
}
